import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FreelancerAutocomplete } from "@/components/freelancers/FreelancerAutocomplete";
import { AgenciaMultiSelect } from "@/components/agencias/AgenciaMultiSelect";
import { useUpsertIndicacoes } from "@/hooks/use-indicacoes-agencia";
import type { Agencia } from "@/types/agencia";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface IndicarAgenciaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando informado, o freelancer fica fixo; senão, mostra um seletor de freelancer. */
  freelancerId?: string;
  freelancerName?: string;
  /** Pré-seleciona a agência (ex.: aberto a partir do detalhe de uma vaga). */
  agenciaIdInicial?: string;
}

/**
 * Diálogo de "Match com Agência": marca manualmente que um freelancer dá match
 * com uma ou mais agências, para ressurgir quando surgir uma vaga delas.
 * Reutilizado no detalhe do freelancer, no painel de candidatos da vaga e na
 * tela de gestão (Radar de Perfis).
 */
export function IndicarAgenciaDialog({
  open,
  onOpenChange,
  freelancerId,
  freelancerName,
  agenciaIdInicial,
}: IndicarAgenciaDialogProps) {
  const { toast } = useToast();
  const upsert = useUpsertIndicacoes();
  const precisaSelecionarFreelancer = !freelancerId;

  const [selFreelancerId, setSelFreelancerId] = useState(freelancerId ?? "");
  const [selAgenciaIds, setSelAgenciaIds] = useState<string[]>([]);
  const [motivo, setMotivo] = useState("");

  const { data: agencias = [] } = useQuery({
    queryKey: ["agencias"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select()
        .order("nome");
      if (error) throw error;
      return (data || []) as Agencia[];
    },
    enabled: open,
  });

  // Reinicia o formulário a cada abertura.
  useEffect(() => {
    if (!open) return;
    setSelFreelancerId(freelancerId ?? "");
    setSelAgenciaIds(agenciaIdInicial ? [agenciaIdInicial] : []);
    setMotivo("");
  }, [open, freelancerId, agenciaIdInicial]);

  const agenciaOptions = useMemo(
    () =>
      agencias.map((a) => ({
        id: a.id,
        nome: a.nome,
        favorita: a.favorita,
      })),
    [agencias],
  );

  const podeSalvar =
    !!selFreelancerId && selAgenciaIds.length > 0 && !upsert.isPending;

  const handleSalvar = async () => {
    if (!podeSalvar) return;
    try {
      await upsert.mutateAsync({
        freelancer_id: selFreelancerId,
        agencia_ids: selAgenciaIds,
        motivo,
      });
      const n = selAgenciaIds.length;
      toast({
        title: n === 1 ? "Match registrado" : "Matches registrados",
        description:
          n === 1
            ? "Match com a agência registrado."
            : `Match com ${n} agências registrado.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar match",
        description: (err as Error).message,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Match com Agência</DialogTitle>
          <DialogDescription>
            {freelancerName
              ? `Marque as agências que dão match com ${freelancerName}.`
              : "Marque as agências que dão match com um freelancer."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {precisaSelecionarFreelancer && (
            <div className="space-y-2">
              <Label>Freelancer</Label>
              <FreelancerAutocomplete
                value={selFreelancerId}
                onValueChange={(id) => setSelFreelancerId(id)}
                placeholder="Buscar freelancer..."
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Agências</Label>
            <AgenciaMultiSelect
              options={agenciaOptions}
              selected={selAgenciaIds}
              onChange={setSelAgenciaIds}
              placeholder="Selecione as agências..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="motivo-indicacao">Motivo (opcional)</Label>
            <Textarea
              id="motivo-indicacao"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: já atuou com esse perfil de cliente, fit de senioridade..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={upsert.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSalvar} disabled={!podeSalvar}>
            {upsert.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
